/**
 * Deep links into the portal (a seilelinje or report URL pasted into an email,
 * say) land on /login whenever the browser has no session. The destination
 * rides along as a `?redirect=` search param so the login page can finish the
 * trip instead of dropping everyone on /mission/overview.
 *
 * Only same-origin paths are honoured. Anything else would make /login an open
 * redirect: a genuine engage.froygruppen.no link that bounces to someone
 * else's copy of the login form the moment the user signs in.
 */

/** Where a sign-in goes when no (usable) destination was carried along. */
export const DEFAULT_LANDING = "/mission/overview";

export function sanitizeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const target = value.trim();
  if (!target.startsWith("/")) return undefined;
  // "//evil.example" and "/\evil.example" are protocol-relative URLs: they
  // start with a slash, but the browser resolves them against another origin.
  if (target.startsWith("//") || target.startsWith("/\\")) return undefined;
  // Control characters are dropped by some URL parsers, which can smuggle a
  // different target past the checks above.
  if ([...target].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) return undefined;
  const path = target.split(/[?#]/)[0];
  // Nothing worth coming back to — "/" bounces onwards anyway, "/login" loops.
  if (path === "/" || path === "/login") return undefined;
  return target;
}
